import React from "react";
import styles from "./source-selection-section.module.scss";
import { MachineNameDropdown } from "@src/components/atoms/machine-name-dropdown/machine-name-dropdown";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import searchIcon from "@src/assets/svg/search.svg";
import saveIcon from "@src/assets/svg/save-disk-icon.svg";
import { useTicketMachineSelection } from "@src/cloud-connect/support/hooks/use-ticket-machine-selection";
import { useI18N } from "@src/language/language-context";
import { SupportAgentTicket } from "@src/types/support";

const cssClassName = "source-selection-section";

const NOOP_REFRESH = () => {
  /* no refresh needed */
};

interface MachineSelectionSectionProps {
  ticketId: string;
  ticketMachineId: string | null;
  callbackForRefresh?: (ticket?: SupportAgentTicket) => void;
  isSaveDisabled?: boolean;
  machineLabel: string;
  machinePlaceholder: string;
  noMachinesFound: string;
  confirmButton: string;
}

export const MachineSelectionSection = ({
  ticketId,
  ticketMachineId,
  callbackForRefresh,
  isSaveDisabled = false,
  machineLabel,
  machinePlaceholder,
  noMachinesFound,
  confirmButton,
}: MachineSelectionSectionProps) => {
  const i18n = useI18N();
  const {
    machineOptions,
    selectedMachine,
    isLoading,
    isSelectionFixed,
    isPatchingMachine,
    isSSEUnavailable,
    onSelectionChange,
    confirmMachineSelection,
  } = useTicketMachineSelection(
    ticketId,
    ticketMachineId,
    callbackForRefresh ?? NOOP_REFRESH,
  );

  const disabledTooltip = isSSEUnavailable
    ? i18n("modules.sse.reconnectingActionsUnavailableTooltip")
    : undefined;

  return (
    <div className={styles[cssClassName]} title={disabledTooltip}>
      <p className={styles[`${cssClassName}__label`]}>{machineLabel}</p>
      <div className={styles[`${cssClassName}__controls`]}>
        <div className={styles[`${cssClassName}__machine-field`]}>
          <img
            className={styles[`${cssClassName}__machine-icon`]}
            src={searchIcon}
            alt=""
            aria-hidden="true"
          />
          <MachineNameDropdown
            options={machineOptions}
            selectedMachine={selectedMachine}
            onSelectMachine={onSelectionChange}
            machineLabelText={machineLabel}
            hideLabel
            showDropdownToggle
            noMachineNamesText={noMachinesFound}
            placeholderText={machinePlaceholder}
            isLoading={isLoading}
            disabled={isSelectionFixed || isPatchingMachine || isSSEUnavailable}
          />
        </div>
        <ButtonWithLoader
          fun={(e) => {
            e.preventDefault();
            void confirmMachineSelection();
          }}
          tooltip={disabledTooltip || confirmButton}
          aria-label={confirmButton}
          variant="no-outline"
          loading={isPatchingMachine}
          disabled={
            isSelectionFixed ||
            isSSEUnavailable ||
            !selectedMachine ||
            isSaveDisabled
          }
          type="button"
          style={{ height: 40, padding: 0, width: 40 }}
        >
          <img
            className={styles[`${cssClassName}__save-icon`]}
            src={saveIcon}
            alt=""
            aria-hidden="true"
          />
        </ButtonWithLoader>
      </div>
    </div>
  );
};
